import React from 'react';
import { Loader2 } from 'lucide-react';
import type { AdditionalRecipient, NotificationPreview, NotificationSetting } from '@/services/notificationSettingsService';
import type { RecipientConfigV1, RecipientTarget } from './types';
import {
  codeOwnedRecipientConfigFor,
  effectiveRecipientConfigFor,
  getTargetLabel,
  recipientContractFor
} from './config';

interface EmailPreviewPaneProps {
  preview: NotificationPreview | null;
  loading: boolean;
  error: string | null;
  setting: NotificationSetting;
  draftConfig: RecipientConfigV1 | null;
  availableUsers: AdditionalRecipient[];
}

function labels(targets: RecipientTarget[], users: AdditionalRecipient[]): string {
  const userLabels = new Map(users.map((user) => [user.id, user.full_name || user.email]));
  return targets.map((target) => getTargetLabel(target, target.kind === 'user' ? userLabels.get(target.user_id) : undefined)).join(', ');
}

export function EmailPreviewPane({ preview, loading, error, setting, draftConfig, availableUsers }: EmailPreviewPaneProps) {
  const contract = recipientContractFor(setting.notification_type);
  const resetPending = draftConfig === null && setting.recipient_config !== null;
  const config = draftConfig ?? (
    resetPending
      ? codeOwnedRecipientConfigFor(setting)
      : effectiveRecipientConfigFor(setting, null)
  );
  const configuredTo = labels(config.to, availableUsers);
  const configuredCc = labels(config.cc, availableUsers);
  const automaticTo = contract?.lockedTo.map((route) => route.label) ?? [];
  const automaticCc = contract?.lockedCc.map((route) => route.label) ?? [];
  const toParts = [...automaticTo, configuredTo].filter(Boolean);
  const ccParts = [...automaticCc, configuredCc].filter(Boolean);
  if (
    toParts.length === 0
    && contract?.primaryMode === 'code_default_until_overridden'
    && setting.recipient_config === null
  ) {
    toParts.push(contract.defaultSummary);
  }
  const toLabel = toParts.join(', ') || setting.recipients;
  const ccLabel = ccParts.join(', ');

  return (
    <aside className="en-preview-pane">
      <div className="en-section-title">
        <span>Email preview</span>
        <span className="en-hint">Sample data shown</span>
      </div>
      <div className="en-preview-frame">
        <div className="en-preview-toolbar">
          <div className="en-preview-dots">
            <span className="en-preview-dot" />
            <span className="en-preview-dot" />
            <span className="en-preview-dot" />
          </div>
          <div>From: notifications@lendwise.com</div>
        </div>
        <div className="en-preview-meta">
          <div className="en-preview-meta-row">
            <span className="en-preview-meta-key">To</span>
            <span className="truncate">{toLabel || '-'}</span>
          </div>
          {ccLabel && (
            <div className="en-preview-meta-row">
              <span className="en-preview-meta-key">Cc</span>
              <span className="truncate">{ccLabel}</span>
            </div>
          )}
          <div className="en-preview-meta-row">
            <span className="en-preview-meta-key">Subject</span>
            <span className="truncate font-semibold">{preview?.subject || 'Select a notification'}</span>
          </div>
        </div>
        <div>
          {loading ? (
            <div className="flex h-[520px] items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="m-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : preview ? (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={preview.html}
              className="en-preview-iframe"
            />
          ) : (
            <div className="m-4 rounded-md border bg-background p-4 text-sm text-muted-foreground">
              Choose a notification to view the rendered email.
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
